/**
 * Readers for synced `data` objects. Another client wrote them, so every field is checked; the
 * per-type modules decide what makes an item unusable (sync contract: readers drop those items).
 */
import type { Nutrition } from '../lib/nutrition'

export type JsonObject = Record<string, unknown>

export function isRecord(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function asRecord(value: unknown): JsonObject {
  return isRecord(value) ? value : {}
}

/** A finite number, or null. */
export function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/** A whole number, or null. */
export function int(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null
}

/** A string, or null. */
export function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

/** A string with something other than spaces, trimmed; null otherwise. */
export function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

export function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

/** [value] when it is one of [allowed], otherwise [fallback]. */
export function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback
}

/** The strings of an array, skipping anything else. */
export function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []
}

/** ISO weekdays (1 = Monday … 7 = Sunday), unique and sorted. */
export function weekdays(value: unknown): number[] {
  if (!Array.isArray(value)) return []
  const days = value.filter((day): day is number => Number.isInteger(day) && day >= 1 && day <= 7)
  return [...new Set(days)].sort((a, b) => a - b)
}

export const MINUTES_PER_DAY = 24 * 60

export function isMinute(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 0 && (value as number) < MINUTES_PER_DAY
}

/** Energy and macros per 100 g (the contract's `Per100g` building block). */
export interface Per100g extends Nutrition {
  sugar?: number | null
  satFat?: number | null
  sodiumMg?: number | null
  /** Grams of ethanol per 100 g (7 kcal/g). */
  alcohol?: number | null
  /** Nutrient key → amount per 100 g; missing means unknown, never zero. */
  micros?: Record<string, number>
}

const OPTIONAL_PER_100G = ['sugar', 'satFat', 'sodiumMg', 'alcohol'] as const

/** Per-100 g values, or null when one of kcal, protein, fat and carbs is missing or negative. */
export function readPer100g(value: unknown): Per100g | null {
  if (!isRecord(value)) return null
  const required = [num(value.kcal), num(value.protein), num(value.fat), num(value.carbs)]
  if (required.some((amount) => amount === null || amount < 0)) return null
  const [kcal, protein, fat, carbs] = required as [number, number, number, number]
  const fiber = num(value.fiber)
  const per100g: Per100g = { kcal, protein, fat, carbs, fiber: fiber !== null && fiber >= 0 ? fiber : null }
  for (const key of OPTIONAL_PER_100G) {
    const amount = num(value[key])
    if (amount !== null && amount >= 0) per100g[key] = amount
  }
  if (isRecord(value.micros)) {
    const micros: Record<string, number> = {}
    for (const [key, amount] of Object.entries(value.micros)) {
      if (typeof amount === 'number' && Number.isFinite(amount) && amount >= 0) micros[key] = amount
    }
    if (Object.keys(micros).length > 0) per100g.micros = micros
  }
  return per100g
}

/** The JSON form of per-100 g values: the four macros, fibre, and the optional extras that are known. */
export function per100gData(per100g: Per100g): JsonObject {
  const data: JsonObject = {
    kcal: per100g.kcal,
    protein: per100g.protein,
    fat: per100g.fat,
    carbs: per100g.carbs,
    fiber: per100g.fiber,
  }
  for (const key of OPTIONAL_PER_100G) {
    const amount = per100g[key]
    if (amount !== undefined && amount !== null) data[key] = amount
  }
  if (per100g.micros && Object.keys(per100g.micros).length > 0) data.micros = { ...per100g.micros }
  return data
}

/** A household portion: `{"label": "1 cup", "grams": 236.0}`. */
export interface Portion {
  label: string
  grams: number
}

/** The valid portions in a list, in order; a portion needs a label and a positive weight. */
export function readPortions(value: unknown): Portion[] {
  if (!Array.isArray(value)) return []
  const portions: Portion[] = []
  for (const entry of value) {
    if (!isRecord(entry)) continue
    const label = text(entry.label)
    const grams = num(entry.grams)
    if (label !== null && grams !== null && grams > 0) portions.push({ label, grams })
  }
  return portions
}

export const FOOD_SOURCES = ['database', 'quick', 'barcode', 'custom', 'ai', 'recipe', 'label'] as const
export type FoodSource = (typeof FOOD_SOURCES)[number]

/** Readers treat unknown sources as `database`. */
export function readSource(value: unknown): FoodSource {
  return oneOf(value, FOOD_SOURCES, 'database')
}

/** Most an entry or ingredient may weigh (sync contract). */
export const MAX_GRAMS = 10_000

export function isGrams(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= MAX_GRAMS
}

/**
 * A food as it is offered for logging (`FoodRef`): what it is, its per-100 g snapshot, its portions,
 * and a default amount (`grams`, entered as `label` of `portionGrams` g, or in grams when both are null).
 */
export interface FoodRef {
  source: FoodSource
  foodId: string | null
  name: string
  brand: string | null
  per100g: Per100g
  portions: Portion[]
  grams: number
  label: string | null
  portionGrams: number | null
}

/** A FoodRef, or null when it has no name, no valid per-100 g values or an amount out of range. */
export function readFoodRef(value: unknown): FoodRef | null {
  if (!isRecord(value)) return null
  const name = text(value.name)
  const per100g = readPer100g(value.per100g)
  const grams = num(value.grams)
  if (name === null || per100g === null || !isGrams(grams)) return null
  const { label, portionGrams } = readEnteredPortion(value)
  return {
    source: readSource(value.source),
    foodId: str(value.foodId),
    name,
    brand: text(value.brand),
    per100g,
    portions: readPortions(value.portions),
    grams,
    label,
    portionGrams,
  }
}

/** The valid FoodRefs in a list, in order. */
export function readFoodRefs(value: unknown): FoodRef[] {
  if (!Array.isArray(value)) return []
  return value.map(readFoodRef).filter((ref): ref is FoodRef => ref !== null)
}

/** `label` and `portionGrams` come as a pair: both set, or both null (amount typed in grams). */
export function readEnteredPortion(value: JsonObject): { label: string | null; portionGrams: number | null } {
  const label = text(value.label)
  const portionGrams = num(value.portionGrams)
  if (label === null || portionGrams === null || portionGrams <= 0) return { label: null, portionGrams: null }
  return { label, portionGrams }
}

export function foodRefData(ref: FoodRef): JsonObject {
  return {
    source: ref.source,
    foodId: ref.foodId,
    name: ref.name,
    brand: ref.brand,
    per100g: per100gData(ref.per100g),
    portions: ref.portions.map((portion) => ({ label: portion.label, grams: portion.grams })),
    grams: ref.grams,
    label: ref.label,
    portionGrams: ref.portionGrams,
  }
}
